import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Button, Card, DatePicker, Form, Input, InputNumber, message, Modal,
  Popconfirm, Select, Space, Table, Tag, Typography,
} from "antd";
import {
  CheckCircleOutlined, EditOutlined, ReloadOutlined, UnlockOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { hasPermission } from "../auth/permissions";
import { listAllActiveBankAccounts } from "../services/bankAccountApi";
import {
  closeReconciliation, listReconciliations, reopenReconciliation, saveReconciliation,
} from "../services/reconciliationApi";

const MONEY = new Intl.NumberFormat("vi-VN", {
  style: "currency",
  currency: "VND",
  maximumFractionDigits: 0,
});

export default function Reconciliations({ currentUser }) {
  const canWrite = hasPermission(currentUser, "reconciliations:edit");
  const canClose = canWrite && ["ADMIN", "SUPER_ADMIN"].includes(currentUser?.role);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [range, setRange] = useState([moment().startOf("month"), moment()]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [reopening, setReopening] = useState(null);
  const sequence = useRef(0);
  const [entryForm] = Form.useForm();
  const [reopenForm] = Form.useForm();

  async function load(nextAccountId = accountId, nextRange = range) {
    if (!nextAccountId || !nextRange?.[0] || !nextRange?.[1]) {
      setRows([]);
      return;
    }
    const request = ++sequence.current;
    setLoading(true);
    setError("");
    try {
      const result = await listReconciliations({
        bankAccountId: nextAccountId,
        from: nextRange[0].format("YYYY-MM-DD"),
        to: nextRange[1].format("YYYY-MM-DD"),
      });
      if (request === sequence.current) setRows(result || []);
    } catch (loadError) {
      if (request === sequence.current) {
        setError(loadError.message || "Không thể tải dữ liệu đối soát");
      }
    } finally {
      if (request === sequence.current) setLoading(false);
    }
  }

  useEffect(() => {
    let mounted = true;
    listAllActiveBankAccounts()
      .then((items) => {
        if (!mounted) return;
        setAccounts(items);
        const firstId = items[0]?.id || "";
        setAccountId(firstId);
        entryForm.setFieldsValue({ date: moment() });
        if (firstId) load(firstId, [moment().startOf("month"), moment()]);
      })
      .catch((loadError) => mounted
        && setError(loadError.message || "Không thể tải tài khoản ngân hàng"));
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function save(values) {
    if (!accountId || saving) return;
    setSaving(true);
    try {
      const saved = await saveReconciliation({
        bankAccountId: accountId,
        date: values.date.format("YYYY-MM-DD"),
        actualBalance: Number(values.actualBalance),
        notes: values.notes?.trim() || null,
      });
      message.success(
        `Đã lưu đối soát, chênh lệch ${MONEY.format(saved.difference)}`
      );
      entryForm.resetFields();
      entryForm.setFieldsValue({ date: moment() });
      await load();
    } catch (saveError) {
      message.error(saveError.message || "Không thể lưu đối soát");
    } finally {
      setSaving(false);
    }
  }

  function editRow(row) {
    entryForm.setFieldsValue({
      date: moment(row.reconciliationDate, "YYYY-MM-DD"),
      actualBalance: row.actualBalance,
      notes: row.notes,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function closeRow(row) {
    setSaving(true);
    try {
      await closeReconciliation(row.id);
      message.success(`Đã chốt đối soát ngày ${moment(row.reconciliationDate).format("DD/MM/YYYY")}`);
      await load();
    } catch (closeError) {
      message.error(closeError.message || "Không thể chốt đối soát");
    } finally {
      setSaving(false);
    }
  }

  async function reopen(values) {
    if (!reopening || saving) return;
    setSaving(true);
    try {
      await reopenReconciliation(reopening.id, values.reason.trim());
      message.success("Đã mở lại ngày đối soát");
      setReopening(null);
      reopenForm.resetFields();
      await load();
    } catch (reopenError) {
      message.error(reopenError.message || "Không thể mở lại đối soát");
    } finally {
      setSaving(false);
    }
  }

  const columns = [
    {
      title: "Ngày",
      dataIndex: "reconciliationDate",
      width: 120,
      render: (value) => moment(value).format("DD/MM/YYYY"),
    },
    {
      title: "Số dư hệ thống",
      dataIndex: "theoreticalBalance",
      align: "right",
      render: (value) => MONEY.format(value),
    },
    {
      title: "Số dư thực tế",
      dataIndex: "actualBalance",
      align: "right",
      render: (value) => MONEY.format(value),
    },
    {
      title: "Chênh lệch",
      dataIndex: "difference",
      align: "right",
      render: (value) => (
        <Typography.Text type={value === 0 ? "success" : "danger"} strong>
          {MONEY.format(value)}
        </Typography.Text>
      ),
    },
    { title: "Ghi chú", dataIndex: "notes", render: (value) => value || "—" },
    {
      title: "Trạng thái",
      dataIndex: "status",
      width: 110,
      render: (value) => (
        <Tag color={value === "CLOSED" ? "green" : "gold"}>
          {value === "CLOSED" ? "Đã chốt" : "Đang mở"}
        </Tag>
      ),
    },
    {
      title: "Thao tác",
      width: 230,
      render: (_, row) => (
        <Space>
          {canWrite && row.status === "OPEN" && (
            <Button type="link" icon={<EditOutlined />} onClick={() => editRow(row)}>
              Sửa số dư
            </Button>
          )}
          {canClose && row.status === "OPEN" && (
            <Popconfirm
              title="Chốt ngày này? Nhân viên nhập liệu sẽ không thể sửa giao dịch của ngày đã chốt."
              okText="Chốt ngày"
              cancelText="Hủy"
              onConfirm={() => closeRow(row)}
            >
              <Button type="link" icon={<CheckCircleOutlined />}>Chốt</Button>
            </Popconfirm>
          )}
          {canClose && row.status === "CLOSED" && (
            <Button type="link" icon={<UnlockOutlined />} onClick={() => setReopening(row)}>
              Mở lại
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="management-page">
      <div className="page-heading">
        <div>
          <Typography.Title level={2}>Đối soát ngày</Typography.Title>
          <Typography.Text type="secondary">
            So sánh số dư thực tế với sổ giao dịch và khóa ngày sau khi đã kiểm tra.
          </Typography.Text>
        </div>
      </div>

      <div className="filter-bar reconciliation-history-filters">
        <div className="filter-field">
          <Typography.Text strong>Tài khoản</Typography.Text>
          <Select
            showSearch
            optionFilterProp="children"
            value={accountId || undefined}
            placeholder="Chọn tài khoản"
            style={{ minWidth: 320 }}
            onChange={(value) => {
              setAccountId(value);
              load(value, range);
            }}
          >
            {accounts.map((account) => (
              <Select.Option key={account.id} value={account.id}>
                {account.internalCode} · {account.bankName} · {account.accountNumber || account.accountNumberMasked || "—"}
              </Select.Option>
            ))}
          </Select>
        </div>
        <div className="filter-field">
          <Typography.Text strong>Khoảng ngày xem lịch sử</Typography.Text>
          <DatePicker.RangePicker
            value={range}
            format="DD/MM/YYYY"
            onChange={(value) => {
              setRange(value);
              if (value) load(accountId, value);
            }}
          />
        </div>
        <Button
          className="history-filter-submit"
          icon={<ReloadOutlined />}
          onClick={() => load()}
        >
          Xem lịch sử
        </Button>
      </div>

      {canWrite && accountId && (
        <Card title="Nhập số dư thực tế" style={{ marginBottom: 16 }}>
          <Form form={entryForm} layout="inline" onFinish={save}>
            <Form.Item
              name="date"
              label="Ngày"
              rules={[{ required: true, message: "Chọn ngày đối soát" }]}
            >
              <DatePicker format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item
              name="actualBalance"
              label="Số dư thực tế"
              rules={[{ required: true, message: "Nhập số dư thực tế" }]}
            >
              <InputNumber
                precision={0}
                style={{ width: 220 }}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                parser={(value) => value.replace(/\./g, "")}
              />
            </Form.Item>
            <Form.Item name="notes" label="Ghi chú">
              <Input placeholder="Ví dụ: Đã kiểm tra sao kê" style={{ width: 280 }} maxLength={1000} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={saving}>Lưu đối soát</Button>
            </Form.Item>
          </Form>
        </Card>
      )}

      {error && <Alert className="page-alert" type="error" showIcon message={error} />}
      <Table
        rowKey="id"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
        scroll={{ x: 1050 }}
        locale={{ emptyText: accountId ? "Chưa có dữ liệu đối soát" : "Chưa có tài khoản được phân công" }}
      />

      <Modal
        visible={Boolean(reopening)}
        title={`Mở lại đối soát ngày ${reopening ? moment(reopening.reconciliationDate).format("DD/MM/YYYY") : ""}`}
        okText="Mở lại"
        cancelText="Hủy"
        confirmLoading={saving}
        onOk={() => reopenForm.submit()}
        onCancel={() => !saving && setReopening(null)}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          message="Nhân viên nhập liệu sẽ có thể sửa giao dịch của ngày này sau khi mở lại."
        />
        <Form form={reopenForm} layout="vertical" onFinish={reopen} style={{ marginTop: 16 }}>
          <Form.Item
            name="reason"
            label="Lý do mở lại"
            rules={[
              { required: true, whitespace: true, message: "Nhập lý do mở lại" },
              { max: 500, message: "Tối đa 500 ký tự" },
            ]}
          >
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
